import {
  CHARACTER_WHITESPACE,
  EMPTY_LIST,
} from '../../../../../../constants/common'
import {
  getListingFlatPublishedListingTitle,
  getListingPortalLabel,
  getListingPublicationErrorTextKey,
  getListingPublishBlockReason,
  LISTING_FLAT_ALREADY_PUBLISHED_ERROR_CODE,
  LISTING_FORM_FIELD_NAME_SCHEDULED_DEACTIVATE,
  LISTING_FORM_FIELD_NAME_SCHEDULED_GO_LIVE,
  LISTING_PORTAL_SYNC_STATUS,
  LISTING_PUBLICATION_IN_PROGRESS_ERROR_CODE,
  LISTING_PUBLICATION_IN_PROGRESS_ERROR_KEY,
  LISTING_PUBLICATION_SYNC_STATUSES,
  LISTING_PUBLICATION_UNKNOWN_SYNC_STATUS,
  LISTING_READINESS_LABEL_KEY,
} from '../../../../../RentalPortalListing'
import {
  getIsListingFormScheduled,
  getListingScheduleValue,
} from '../../RentalPortalListingsForm.utils'

import {
  LISTING_NOT_READY_ERROR_CODE,
  LISTING_PORTAL_NOT_CONNECTED_ERROR_CODE,
} from './useRentalPortalListingsFormPublish.constants'

function getListingPublicationStatusTextKey(publication) {
  return (
    LISTING_PUBLICATION_SYNC_STATUSES[publication.sync_status] ??
    LISTING_PUBLICATION_UNKNOWN_SYNC_STATUS
  ).text
}

function getIsListingPublicationSyncFailed(publication) {
  return publication.sync_status === LISTING_PORTAL_SYNC_STATUS.error
}

function getIsFailedListingPublication(publication) {
  return (
    Boolean(publication.error) || getIsListingPublicationSyncFailed(publication)
  )
}

export function getListingFormScheduleValues({
  hasPortalsToPublish,
  record,
  values,
}) {
  const isSchedulable = getIsListingFormScheduled(values) && hasPortalsToPublish

  return {
    goLive: isSchedulable
      ? getListingScheduleValue(
          values[LISTING_FORM_FIELD_NAME_SCHEDULED_GO_LIVE]
        )
      : null,
    hasPersistedSchedule: Boolean(
      record.scheduled_go_live || record.scheduled_deactivate
    ),
    scheduledDeactivate: isSchedulable
      ? getListingScheduleValue(
          values[LISTING_FORM_FIELD_NAME_SCHEDULED_DEACTIVATE]
        )
      : null,
  }
}

export function getListingPublishNotificationTitleKey(values) {
  return values[LISTING_FORM_FIELD_NAME_SCHEDULED_GO_LIVE]
    ? 'RentalPortal.ListingFormPublishScheduledNotificationKey'
    : 'RentalPortal.ListingFormPublishStartedNotificationKey'
}

export function getListingPublishOutcomes(publications) {
  return {
    failed: publications.filter(getIsFailedListingPublication),
    succeeded: publications.filter(
      publication => !getIsFailedListingPublication(publication)
    ),
  }
}

export function getListingPublishSucceededText({ publications, t }) {
  return publications
    .map(publication =>
      t('RentalPortal.ListingFormPublishPortalOutcomeKey', {
        outcome: t(getListingPublicationStatusTextKey(publication)),
        portal: getListingPortalLabel(publication.portal),
      })
    )
    .join(', ')
}

export function getListingPublishFailedText({ publications, t }) {
  return publications
    .map(publication =>
      t(getListingPublicationErrorTextKey(publication), {
        portal: getListingPortalLabel(publication.portal),
      })
    )
    .join(CHARACTER_WHITESPACE)
}

export function getListingPublishFailedPortals(publications) {
  return publications
    .map(publication => getListingPortalLabel(publication.portal))
    .join(', ')
}

export function getListingPublishFailureText({ publications, t }) {
  return publications.length > 0
    ? t('RentalPortal.ListingFormPublishNotPublishedErrorKey', {
        portals: getListingPublishFailedPortals(publications),
        reasons: getListingPublishFailedText({ publications, t }),
      })
    : t('RentalPortal.ListingFormPublishErrorStartKey')
}

export function getListingPublishErrorText({ error, t }) {
  switch (error?.response?.data?.errors) {
    case LISTING_NOT_READY_ERROR_CODE:
      return (
        getListingPublishBlockReason({
          missingItems: (error.response.data.meta?.missing ?? EMPTY_LIST)
            .map(key => LISTING_READINESS_LABEL_KEY[key])
            .filter(Boolean)
            .map(labelKey => t(labelKey)),
          t,
        }) ?? t('RentalPortal.ListingFormPublishErrorStartKey')
      )
    case LISTING_PORTAL_NOT_CONNECTED_ERROR_CODE:
      return t('RentalPortal.ListingFormPublishNotConnectedKey')
    case LISTING_FLAT_ALREADY_PUBLISHED_ERROR_CODE:
      return t('RentalPortal.ListingFormFlatHasPublishedErrorKey', {
        title: getListingFlatPublishedListingTitle({
          flatPublishedListing: error.response.data.meta?.listing,
          t,
        }),
      })
    case LISTING_PUBLICATION_IN_PROGRESS_ERROR_CODE:
      return t(LISTING_PUBLICATION_IN_PROGRESS_ERROR_KEY)
    default:
      return t('RentalPortal.ListingFormPublishErrorStartKey')
  }
}
